/**
 * One thing a coach hasn't opened yet.
 *
 * Most of the platform is never found by most people, and the usual fix is a
 * feature tour on day one, which is the worst possible day to be told about
 * fourteen things. This is the slow version: remember which pages a coach has
 * actually opened, and once in a while point at one they haven't.
 *
 * The rules that keep it from becoming noise:
 *
 *   One at a time. A list of things you're not doing is a chore, not a nudge.
 *
 *   Nothing for the first week. Somebody who joined on Tuesday hasn't opened
 *   most of the platform because they've been here two days, not because they
 *   missed it.
 *
 *   Dismissing is permanent for that entry. "Not interested" said once should
 *   not have to be said twice.
 *
 *   Only pages. Whether somebody has used the command palette is not
 *   something we can see, and guessing wrong means nagging a coach about a
 *   thing they do every day.
 *
 * Visits live in user_data so they follow a coach across devices: opening the
 * Playbook on a laptop shouldn't leave the phone still suggesting it.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { loadUserData, saveUserData } from "../utils/userData";
import { nudgeable } from "../constants/platformMap";

const KEY = "explored";
const QUIET_DAYS = 7;

// The visit keys the registry knows about. A route that isn't one of these
// (a film id, an admin sub-page, a cert unit) is not a page a coach "opened"
// in the sense this hook cares about, and stamping every one would grow the
// visited map by one key per film ever watched.
const KNOWN = new Set(nudgeable().map(e => e.visitKey));

export function useUnexplored(nav, { enabled = true, joinedAt, available } = {}) {
  const [state, setState] = useState(null);   // { visited: {}, dismissed: {} }
  const stampedRef = useRef(new Set());

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    (async () => {
      const raw = await loadUserData(KEY, {});
      if (!live) return;
      setState({
        visited: (raw && raw.visited) || {},
        dismissed: (raw && raw.dismissed) || {},
      });
    })();
    return () => { live = false; };
  }, [enabled]);

  // Stamp the page they're on. Best-effort: a lost write costs one repeated
  // suggestion, which is cheaper than blocking navigation on a round trip.
  useEffect(() => {
    if (!enabled || !nav || !state) return;
    // "film/abc123" is a visit to film. "certified/unit-3" is a visit to certified.
    const key = String(nav).split("/")[0];
    if (!KNOWN.has(key)) return;
    if (stampedRef.current.has(key)) return;
    if (state.visited[key]) { stampedRef.current.add(key); return; }

    stampedRef.current.add(key);
    const next = { ...state, visited: { ...state.visited, [key]: Date.now() } };
    setState(next);
    saveUserData(KEY, next).catch(() => {});
  }, [nav, enabled, state]);

  const dismiss = useCallback((id) => {
    setState(prev => {
      if (!prev) return prev;
      const next = { ...prev, dismissed: { ...prev.dismissed, [id]: Date.now() } };
      saveUserData(KEY, next).catch(() => {});
      return next;
    });
  }, []);

  if (!enabled || !state) return { suggestion: null, dismiss };

  // A week's grace. Long enough that "you haven't opened this" is a fair
  // observation rather than a statement about how new they are.
  if (joinedAt) {
    const age = Date.now() - new Date(joinedAt).getTime();
    if (Number.isFinite(age) && age < QUIET_DAYS * 24 * 3600 * 1000) {
      return { suggestion: null, dismiss };
    }
  }

  // Only pages this account can reach. Suggesting the gated Lab to a coach
  // who can't open it is the "door onto an empty room" this exists to stop.
  const open = nudgeable().filter(e =>
    !state.visited[e.visitKey] && !state.dismissed[e.id] &&
    (!available || available.has(e.visitKey)));
  if (open.length === 0) return { suggestion: null, dismiss };

  // Registry order, so the first suggestions are the ones a coach is most
  // likely to want: practice first, then craft, then everything else. Stable
  // between sessions, so it doesn't shuffle underneath them.
  return { suggestion: open[0], dismiss };
}

export default useUnexplored;
